import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import globals from 'scoreboard/components/globals'
import Dataset from 'scoreboard/datasets/model/dataset'
import DashboardData from 'scoreboard/dashboards/dashboard-data'
import { updateDesignerFromFetch } from '../charts-view'

/**
 * Provides chart data, relevant domain object data, and fetch capabilities
 * for chart designers
 *
 * ChartData is the data that's been crunched and put into the chart module's
 * API format (plus some scorecard/initiative/dataset metadata). It really
 * should only ever be used by the chart module (or getting domain object
 * metadata).
 */
class ChartData {

	/**
	 * @param {Object} [options]
	 *
	 * @param {Object} [options.data] initial data to use (so a fetch isn't needed)
	 *
	 * @param {DashboardData} [dashboardData] for a chart on a dashboard, the
	 * corresponding DashboardData. Used as an alternate source for dataset metadata
	 */
	constructor (options = {}) {
		this._data = options.data || { plots: [], datasets: {} }
		this._datasetModels = new Map()
		/** @type {DashboardData} */
		this._dashboardData = options.dashboardData || new DashboardData()
	}

	/**
	 * Fetches fresh chart data
	 *
	 * @param {Object} options
	 *
	 * @param {ChartDesigner} [chart] if provided, the chart designer will be
	 * sent to the server and used for gathering the data (as opposed to using
	 * the chart state saved in the database)
	 *
	 * @param {Number} [chartId] if provided, the ID of the chart designer to
	 * be used for fetching the data; this will use the chart state saved in
	 * the server
	 *
	 * @param {Number} [periodId] the period ID to use for fetching the
	 * data. If not provided, globals.periodId will be used
	 *
	 * @param {ChartDesignerDrilldown} [drilldown] information on a chart drilldown
	 * (filters, drilldown ID, etc)
	 *
	 * @return {Promise} a Promise that will resolve when the fetch is complete.
	 * An updated ChartDesigner from the server will be passed to the callback;
	 * the ChartDesigner may have updated value suggestions, errors, etc
	 */
	fetch (options = {}) {
		_.invoke(this.xhr, 'abort')

		const idPartOrSlash = _.isNil(options.chartId) ? '/' : `/${options.chartId}/`

		let promise = new Promise((resolve) => {
			let periodId = options.periodId ?? globals.periodId
			let url = `api/charts${idPartOrSlash}data?calendarPeriodId=${periodId}`

			let xhrOptions = {
				method: 'POST',
				success: (response) => {
					this.currentDrilldown(options.drilldown)
					this.set(response.chartData)

					updateDesignerFromFetch({
						oldDesigner: options.chart,
						updatedDesigner: response.chartDesigner
					})

					resolve({ updatedChartDesigner: response.chartDesigner })
					this.trigger('fetch:success')
				}
			}

			if (options.chart) {
				// Clear the error out so it doesn't keep displaying; the server
				// will repopulate it if it's still a problem
				const chart = options.chart
				// Don't want a change event, though, because it will cause a
				// re-render and we may get in a race condition with the fetch
				chart.unset('error', { silent: true })
				xhrOptions.data = JSON.stringify({
					chartDesigner: chart.toPostJSON(),
					liveFilters: options.liveFilters,
					drilldownId: options.drilldownId
				})
			}

			this.xhr = $.ajax(url, xhrOptions)
		})

		return promise
	}

	/**
	 * Returns or sets the plots for the chart data (the primary data used in actually
	 * rendering the chart)
	 */
	plots (newPlots) {
		if (!this._data) {
			this._data = {}
		}
		if (arguments.length === 0) {
			return this._data.plots || []
		} else {
			this._data.plots = newPlots
		}
	}

	/**
	 * Get all domain objects of a given type, or a single object by id.
	 *
	 * @param {String} section e.g. 'scorecards', use metadata to get section from domainType
	 *
	 * @param {Number} [id] the domain object id to retrieve (not a Backbone.Model)
	 *
	 * @param {Object} [newVal] Sets this object in the internal data so it will
	 * be retrieved on future calls to domainObject()
	 *
	 * @returns {Object[]|Object} If only given a section, returns all domain object data for that section
	 * (ex. all scorecard items).  If an id is provided a single domain object is returned
	 */
	domainObject (section, id, newVal) {
		if (arguments.length === 1) {
			return this._data[section] || {}
		}

		if (_.isNil(newVal)) {
			return _.get(this._data, `${section}[${id}]`)
		} else {
			if (!this._data[section]) {
				this._data[section] = {}
			}

			this._data[section][id] = newVal
		}
	}

	/**
	 * Returns or sets dataset data (not Dataset models). See domainObject()
	 * for details on arguments.
	 */
	datasets (id, newVal) {
		// If setting a new dataset object, clear out the cached Dataset model
		if (arguments.length === 2) {
			this._datasetModels.delete(id)
		}

		// #46490 check the DashboardData first; charts on dashboards keep their
		// datasets in DashboardData, so they aren't duplicated across multiple charts
		if (arguments.length == 1) {
			let ds = this._dashboardData.datasets(id)
			if (ds) {
				return ds
			}
		}

		if (arguments.length == 0) {
			// #46490 if asking for all datasets, make sure we merge the
			// maps for both ChartData and DashboardData

			// Intentionally using a shallow clone; we don't want to mutate the
			// map/object, but we also don't want to clone the dataset metadata
			let datasets = _.clone(this.domainObject('datasets'))
			if (this._dashboardData) {
				_.toPairs(this._dashboardData.datasets()).forEach(pair => {
					datasets[pair[0]] = pair[1]
				})
			}

			return datasets
		}

		return this.domainObject.apply(this, ['datasets'].concat(Array.from(arguments)))
	}

	/**
	 * Returns a Dataset model for the given dataset ID. This keeps an internal cache
	 * because the Dataset model can be expensive to build for datasets with lots of
	 * fields and links
	 *
	 * If a dataset is provided, will add it to this ChartData
	 *
	 * @return {Dataset}
	 */
	datasetModel (id, dataset) {
		if (arguments.length == 2) {
			this.datasets(id, dataset.toJSON())
			this._datasetModels.set('id', dataset)
			return
		}

		// #46490 check the DashboardData first; charts on dashboards keep their
		// datasets in DashboardData, so they aren't duplicated across multiple charts
		let dashDataset = this._dashboardData.datasetModel(id)
		if (dashDataset) {
			return dashDataset
		}

		let model = this._datasetModels.get(id)
		if (model) {
			return model
		}

		let attrs = this.datasets(id)
		if (attrs) {
			let model = new Dataset(attrs)
			this._datasetModels.set(id, model)
			return model
		} else {
			return null
		}
	}

	/**
	 * Returns or sets scorecard item data. See domainObject() for details on arguments.
	 */
	scorecards (id, newVal) {
		return this.domainObject.apply(this, ['scorecards'].concat(Array.from(arguments)))
	}

	/**
	 * Returns or sets initiative data. See domainObject() for details on arguments.
	 */
	initiatives (id, newVal) {
		return this.domainObject.apply(this, ['initiatives'].concat(Array.from(arguments)))
	}

	/**
	 * Returns or sets organization data. See domainObject() for details on arguments.
	 */
	organizations (id, newVal) {
		return this.domainObject.apply(this, ['organizations'].concat(Array.from(arguments)))
	}

	treeItems (id, newVale) {
		return this.domainObject.apply(this, ['treeItems'].concat(Array.from(arguments)))
	}

	/**
	 * If the zoomTo was manually set on the server (like in scenario for #52600),
	 * these IDs should be used when rendering the chart
	 * @returns {Int[]}
	 */
	zoomToIds () {
		return this._data.zoomToIds
	}

	/**
	 * Returns all domain object data for all sections.
	 */
	get () {
		return _.cloneDeep(this._data)
	}

	/**
	 * Replaces all internal domain object data for all sections.
	 */
	set (data) {
		this._data = data
		this._datasetModels.clear()
	}

	/**
	 * If provided, stores the current drilldown state for the chart. If no
	 * state provided, returns the current drilldown state.
	 *
	 * @param {DrilldownState} [current]
	 * @returns {DrilldownState}
	 */
	currentDrilldown (current) {
		if (_.isNil(current)) {
			return this._currentDrilldown
		} else {
			this._currentDrilldown = current
		}
	}

	clearCurrentDrilldown () {
		delete this._currentDrilldown
	}

	rollupParentId () {
		// tree-map charts only have 1 serie
		let serie = this.plots()[0].series[0]
		return serie.rollupParentId
	}

	/**
	 * This only performs a deep copy of _data.plots, since the other data isn't
	 * mutated
	 *
	 * @return {ChartData}
	 */
	quickCopy () {
		let data = _.extend({}, this._data,
			{ plots: _.cloneDeep(this._data.plots) })

		return new ChartData({
			data,
			dashboardData: this._dashboardData
		})
	}
}

_.extend(ChartData.prototype, Backbone.Events)

export default ChartData

ChartData.__filename__ = 'charts/chart-data/chart-data.js'
